// One order at the gate (reached by scanning its QR, or from Orders): where to send the truck, whether the
// requester has been notified, and every consignment of this order so far with its status. A scan at a store
// set to "notify on scan" has already logged the arrival and notified (shown here); otherwise — or for a
// second truck — the guard taps "Notify requester". Nothing else is asked of the guard.
// Phone: destination → notify → history. Wider screens: order + history left, notify right.

import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, BellRing, CheckCircle2, ClipboardList, Loader2, MapPin, Phone, ScanQrCode } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, type GateArrival, type OrderView } from "@/lib/gate";
import { playScanSound } from "@/lib/scan-sounds";
import { useGate } from "@/lib/session-scope";
import { ConsignmentCard } from "@/components/consignment-card";
import { ConsignmentSheet } from "@/components/consignment-sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function OrderPage() {
  const { id } = useParams();
  const poId = Number(id);
  const { token } = useAuth();
  const { gate } = useGate();
  const navigate = useNavigate();
  const location = useLocation();
  // Handed over by the scan screen: what the scan already did (notify-on-scan stores).
  const scanState = (location.state ?? null) as { arrival?: GateArrival | null; arrivalError?: string | null } | null;

  const [order, setOrder] = useState<OrderView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [arrival, setArrival] = useState<GateArrival | null>(scanState?.arrival ?? null);
  const [arrivalError, setArrivalError] = useState<string | null>(scanState?.arrivalError ?? null);
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await gateApi.order(token, gate.id, poId);
      if (d?.errorMessage) setError(d.errorMessage);
      else setOrder(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the order.");
    }
  }, [token, gate.id, poId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- (re)load when the order changes
    setOrder(null);
    setError(null);
    void load();
  }, [load]);

  async function notify() {
    if (!order) return;
    setNotifying(true);
    try {
      const d = await gateApi.recordConsignment(token, gate.id, { purchaseOrderId: order.id });
      if (d?.errorMessage) {
        playScanSound("fail");
        toast.error(d.errorMessage);
        return;
      }
      playScanSound("success");
      navigator.vibrate?.(60);
      setOrder(d);
      setArrivalError(null);
      if (d.recorded) setArrival({ consignment: d.recorded, alreadyNotified: false, note: null });
      toast.success(d?.myMessage ?? "Requester notified.");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not notify the requester.");
    } finally {
      setNotifying(false);
    }
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
        <AlertTriangle className="size-10 text-destructive" />
        <p className="font-semibold">{error}</p>
        <Button variant="outline" nativeButton={false} render={<Link to="/scan" />}>
          <ScanQrCode /> Scan again
        </Button>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="flex flex-col gap-3 px-4 pt-4 md:px-6">
        <Skeleton className="h-16 w-full rounded-2xl" />
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  const destinations = order.destinations ?? [];
  const history = order.consignments ?? [];

  return (
    <div className="flex w-full flex-col gap-4 px-4 pt-4 pb-8 md:px-6">
      <div className="min-w-0">
        <div className="text-lg leading-tight font-semibold">{order.vendor?.name ?? "Purchase order"}</div>
        <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
          <span className="font-mono text-foreground">{order.poNumber}</span>
          {order.store?.name && <span>· {order.store.name}</span>}
          <span>
            ·{" "}
            {history.length
              ? `${history.length} consignment${history.length === 1 ? "" : "s"} so far`
              : "no consignment yet"}
          </span>
        </div>
      </div>

      {order.blocked && (
        <div className="flex items-start gap-2 rounded-2xl bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" /> {order.blocked}
        </div>
      )}

      {/* Phones: destination → this truck's form → history. Wider: destination + history left, form right. */}
      <div className="grid gap-4 md:grid-cols-2 md:grid-rows-[auto_1fr] md:items-start xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-4 md:col-start-1 md:row-start-1">
          <div className="rounded-2xl bg-primary p-4 text-primary-foreground shadow-sm">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase opacity-90">
              <MapPin className="size-4" /> Send the truck to
            </div>
            {destinations.length === 0 ? (
              <p className="text-lg leading-snug font-semibold">{order.store?.name ?? "The store"}</p>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {destinations.map((d, i) => (
                  <li key={d.department?.id ?? i}>
                    <span className="block text-lg leading-snug font-semibold">
                      {d.department?.name ?? order.store?.name}
                    </span>
                    {d.deliverySites && d.deliverySites.length > 0 && (
                      <span className="block text-base">{d.deliverySites.join("; ")}</span>
                    )}
                    {(d.requesters?.length || d.neededBy) && (
                      <span className="block text-xs opacity-85">
                        {[d.requesters?.join(", "), d.neededBy ? `needed by ${d.neededBy}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {order.vendor?.contactNumber && (
            <Button
              variant="outline"
              className="h-11 justify-start rounded-xl bg-card"
              nativeButton={false}
              render={<a href={`tel:${order.vendor.contactNumber.replace(/\s+/g, "")}`} />}
            >
              <Phone /> Call vendor ·{" "}
              {[order.vendor.contactPerson, order.vendor.contactNumber].filter(Boolean).join(" · ")}
            </Button>
          )}
        </div>

        {/* This truck. */}
        <div className="flex min-w-0 flex-col gap-4 md:sticky md:top-4 md:col-start-2 md:row-span-2 md:row-start-1">
          {arrival ? (
            <div className="flex flex-col gap-3 rounded-2xl bg-emerald-100 p-4 text-emerald-950 dark:bg-emerald-950 dark:text-emerald-100">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 size-6 shrink-0" />
                <div>
                  <div className="text-base font-semibold">
                    {arrival.alreadyNotified ? "Requester already notified" : "Requester notified"}
                  </div>
                  <div className="text-sm opacity-85">
                    Consignment {arrival.consignment.consignmentNumber ?? ""}
                    {arrival.consignment.arrivedTime ? ` · ${arrival.consignment.arrivedTime}` : ""}
                    {arrival.alreadyNotified
                      ? " — this order was scanned a few minutes ago, so nobody was emailed twice."
                      : arrival.note
                        ? ` — ${arrival.note}.`
                        : "."}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button className="h-12 rounded-xl" onClick={() => navigate("/scan")}>
                  <ScanQrCode /> Next truck
                </Button>
                <Button
                  variant="outline"
                  className="h-12 rounded-xl bg-card"
                  disabled={notifying}
                  onClick={() => {
                    setArrival(null);
                    setArrivalError(null);
                  }}
                >
                  Another truck, same order
                </Button>
              </div>
            </div>
          ) : order.blocked ? (
            <Button variant="outline" className="h-12 rounded-xl" onClick={() => navigate("/scan")}>
              <ScanQrCode /> Scan another
            </Button>
          ) : (
            <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4">
              {arrivalError && (
                <div className="flex items-start gap-2 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" /> {arrivalError}
                </div>
              )}
              <Button onClick={() => void notify()} disabled={notifying} className="h-14 w-full rounded-2xl text-base shadow-md">
                {notifying ? <Loader2 className="size-5 animate-spin" /> : <BellRing className="size-5" />} Notify requester
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Logs this truck as consignment {order.nextConsignmentNumber ?? 1} and emails the requesting departments and
                the store to come and receive it.
              </p>
            </div>
          )}
        </div>
        {/* History of this order's consignments. */}
        <section className="flex min-w-0 flex-col gap-2.5 md:col-start-1 md:row-start-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <ClipboardList className="size-4 text-primary" /> Consignments of this order
            <span className="rounded-full bg-card px-2 text-xs text-muted-foreground">{history.length}</span>
          </h2>
          {history.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border bg-card p-4 text-sm text-muted-foreground">
              Nothing has come for this order yet — this truck is consignment 1.
            </p>
          ) : (
            history.map((c) => (
              <ConsignmentCard
                key={c.id}
                c={c}
                showOrder={false}
                onOpen={() => setOpenId(c.id)}
                onChanged={() => void load()}
              />
            ))
          )}
        </section>
      </div>

      <ConsignmentSheet id={openId} onClose={() => setOpenId(null)} onChanged={() => void load()} />
    </div>
  );
}
